import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';

import { AlertProvider } from './components/AlertProvider';
import { ConfirmProvider } from './components/ConfirmProvider';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import { Loading } from './components/common/Loading';
import { ROUTE_ROLES } from './config/routeAccess';

const Admin = lazy(() => import('./pages/Admin'));
const Counties = lazy(() => import('./pages/Counties'));
const CountyDetail = lazy(() => import('./pages/CountyDetail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Groups = lazy(() => import('./pages/Groups'));
const Home = lazy(() => import('./pages/Home'));
const LocalPlan = lazy(() => import('./pages/LocalPlan'));
const LocalPlanForm = lazy(() => import('./pages/LocalPlanForm'));
const Login = lazy(() => import('./pages/Login'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const OpportunityDetail = lazy(() => import('./pages/OpportunityDetail'));
const Organizations = lazy(() => import('./pages/Organizations'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Projects = lazy(() => import('./pages/Projects'));
const Questionaries = lazy(() => import('./pages/Questionaries'));
const QuestionaryView = lazy(() => import('./pages/QuestionaryDetail'));
const QuestionaryForm = lazy(() => import('./pages/QuestionaryForm'));
const QuestionaryManagement = lazy(() => import('./pages/QuestionaryManagement'));
const QuestionaryManagementForm = lazy(() => import('./pages/QuestionaryManagementForm'));
const Questions = lazy(() => import('./pages/Questions'));
const RegisterRequest = lazy(() => import('./pages/RegisterRequest'));
const SponsorView = lazy(() => import('./pages/SponsorDetail'));
const Sponsors = lazy(() => import('./pages/Sponsors'));
const UserForm = lazy(() => import('./pages/UserForm'));
const Users = lazy(() => import('./pages/Users'));
const UserSettings = lazy(() => import('./pages/UserSettings'));

function App() {
  return (
    <AlertProvider>
      <ConfirmProvider>
        <Router>
          <Layout>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/home" element={<Home />} />
                {/* Public: guests can browse the dashboard */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register-request" element={<RegisterRequest />} />

                <Route
                  path="/admin"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.admin}>
                      <Admin />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questionaries"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.questionaries}>
                      <Questionaries />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questionary-view/:answerId"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.questionaries}>
                      <QuestionaryView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questionary-form/:questionaryId?/:countyId?"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.questionaries}>
                      <QuestionaryForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questionary-management"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.questionaryManagement}>
                      <QuestionaryManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questionary-manager-form/:id?"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.questionaryManagement}>
                      <QuestionaryManagementForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/questions"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.questions}>
                      <Questions />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/organizations"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.admin}>
                      <Organizations />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/groups"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.admin}>
                      <Groups />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/opportunities"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.opportunities}>
                      <Opportunities />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/opportunities/:opportunityId"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.opportunities}>
                      <OpportunityDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/counties"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.counties}>
                      <Counties />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/counties/:countyId"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.counties}>
                      <CountyDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sponsors"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.sponsors}>
                      <Sponsors />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/sponsors/:sponsorId"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.sponsors}>
                      <SponsorView />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.projects}>
                      <Projects />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects/:projectId"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.projects}>
                      <ProjectDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/localplan"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.localplan}>
                      <LocalPlan />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/localplan-form/:answerId?/:localplanId?"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.localplan}>
                      <LocalPlanForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.admin}>
                      <Users />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/users/add"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.admin}>
                      <UserForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/users/edit/:id"
                  element={
                    <PrivateRoute roles={ROUTE_ROLES.admin}>
                      <UserForm />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <UserSettings />
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Layout>
        </Router>
      </ConfirmProvider>
    </AlertProvider>
  );
}

export default App;
